/**
 * Plain-text e-check validation rules (shared by MUI {@link ECheckForm} and Adyen ACH blur messages).
 * Matches `useTextValidator` configs in ECheckForm.tsx.
 */

export function validateECheckOwnerName(value: string): string | null {
  if (value.length === 0) return 'Name on the account is required.';
  if (value.length > 25) return 'Invalid name on the account.';
  if (/\d{5,}/.test(value)) return 'Invalid name on the account.';
  return null;
}

export function validateECheckRoutingDigits(value: string): string | null {
  if (value.length === 0) return 'Routing number is required.';
  if (value.length !== 9) return 'Routing number should be 9 digits.';
  return null;
}

export function validateECheckAccountDigits(value: string): string | null {
  if (value.length === 0) return 'Account number is required.';
  if (value.length < 5) return 'Account number should be at least 5 digits.';
  return null;
}

export function validateECheckConfirmDigits(confirmValue: string, accountValue: string): string | null {
  if (confirmValue.length === 0) return 'Re-type account number is required.';
  if (confirmValue.length < 5) return 'Re-type account number should be at least 5 digits.';
  if (confirmValue !== accountValue) return 'Account number does not match.';
  return null;
}

/** Adyen ACH `Field` label `data-id` values that participate in custom blur validation. */
export const ACH_BLUR_FIELD_NAMES = [
  'ownerName',
  'routingNumber',
  'accountNumber',
  'accountNumberVerification',
] as const;

export type AchBlurFieldId = (typeof ACH_BLUR_FIELD_NAMES)[number];

function isDigitsOnly(s: string): boolean {
  return s.length > 0 && /^\d+$/.test(s);
}

/** Adyen secured ACH fields expose `valid` / `errors` under encrypted keys (and sometimes plain Ach schema keys). */
function routingValidOk(valid: Record<string, unknown>): boolean {
  return (
    valid.encryptedBankLocationId === true ||
    valid.bankLocationId === true ||
    valid.routingNumber === true
  );
}

function routingInvalidOrErr(valid: Record<string, unknown>, errors: Record<string, unknown>): boolean {
  return (
    valid.encryptedBankLocationId === false ||
    valid.bankLocationId === false ||
    valid.routingNumber === false ||
    !!errors.encryptedBankLocationId ||
    !!errors.bankLocationId ||
    !!errors.routingNumber
  );
}

function accountValidOk(valid: Record<string, unknown>): boolean {
  return (
    valid.encryptedBankAccountNumber === true ||
    valid.bankAccountNumber === true ||
    valid.accountNumber === true
  );
}

function accountInvalidOrErr(valid: Record<string, unknown>, errors: Record<string, unknown>): boolean {
  return (
    valid.encryptedBankAccountNumber === false ||
    valid.bankAccountNumber === false ||
    valid.accountNumber === false ||
    !!errors.encryptedBankAccountNumber ||
    !!errors.bankAccountNumber ||
    !!errors.accountNumber
  );
}

function verifyValidOk(valid: Record<string, unknown>): boolean {
  return (
    valid.encryptedBankAccountNumberVerification === true ||
    valid.accountNumberVerification === true
  );
}

function verifyInvalidOrErr(valid: Record<string, unknown>, errors: Record<string, unknown>): boolean {
  return (
    valid.encryptedBankAccountNumberVerification === false ||
    valid.accountNumberVerification === false ||
    !!errors.encryptedBankAccountNumberVerification ||
    !!errors.accountNumberVerification
  );
}

/**
 * Maps Adyen ACH `onChange` state (session checkout shape) to the same user-facing messages as ECheckForm.
 */
export function eCheckAchFieldBlurMessage(field: AchBlurFieldId, state: any): string | null {
  if (!state) {
    if (field === 'ownerName') return 'Name on the account is required.';
    if (field === 'routingNumber') return 'Routing number is required.';
    if (field === 'accountNumber') return 'Account number is required.';
    if (field === 'accountNumberVerification') return 'Re-type account number is required.';
    return null;
  }

  const data = state.data ?? {};
  const pm = data.paymentMethod ?? {};
  const valid = state.valid ?? {};
  const errors = state.errors ?? {};

  if (field === 'ownerName') {
    return validateECheckOwnerName(String(pm.ownerName ?? data.ownerName ?? ''));
  }

  if (field === 'routingNumber') {
    const enc = pm.encryptedBankLocationId || pm.bankLocationId;
    const plain = String(data.routingNumber ?? pm.routingNumber ?? '');
    if (enc && routingValidOk(valid)) return null;
    if (isDigitsOnly(plain)) {
      return validateECheckRoutingDigits(plain);
    }
    if (!enc) return 'Routing number is required.';
    if (routingInvalidOrErr(valid, errors)) return 'Routing number should be 9 digits.';
    return null;
  }

  if (field === 'accountNumber') {
    const enc = pm.encryptedBankAccountNumber || pm.bankAccountNumber;
    const plain = String(data.accountNumber ?? pm.accountNumber ?? '');
    if (enc && accountValidOk(valid)) return null;
    if (isDigitsOnly(plain)) {
      return validateECheckAccountDigits(plain);
    }
    if (!enc) return 'Account number is required.';
    if (accountInvalidOrErr(valid, errors)) {
      return 'Account number should be at least 5 digits.';
    }
    return null;
  }

  if (field === 'accountNumberVerification') {
    const plainV = String(data.accountNumberVerification ?? pm.accountNumberVerification ?? '');
    const plainA = String(data.accountNumber ?? pm.accountNumber ?? '');
    if (isDigitsOnly(plainV) && (plainA.length === 0 || isDigitsOnly(plainA))) {
      return validateECheckConfirmDigits(plainV, plainA);
    }
    if (verifyValidOk(valid)) return null;

    const verifyErr =
      errors.encryptedBankAccountNumberVerification || errors.accountNumberVerification;
    const hasEncryptedVerify =
      !!pm.encryptedBankAccountNumberVerification || !!pm.bankAccountNumberVerification;

    if (!plainV && !hasEncryptedVerify) {
      return 'Re-type account number is required.';
    }
    if (verifyInvalidOrErr(valid, errors)) {
      const em =
        typeof verifyErr === 'string'
          ? verifyErr
          : String(
              (verifyErr as { errorMessage?: string; message?: string })?.errorMessage ??
                (verifyErr as { message?: string })?.message ??
                '',
            );
      const lower = em.toLowerCase();
      if (lower.includes('empty') || lower.includes('required')) {
        return 'Re-type account number is required.';
      }
      return 'Account number does not match.';
    }
    return null;
  }

  return null;
}
